// macadamia-app/src/macadamia-core/components/widgets/ListWidget.tsx
import React, { useState, useCallback, useEffect } from 'react';
import { IListBlock, IListItem, ISuggestion, ListStyleType } from './types';
import { useDossier } from '../../dossierEditor/DossierContext';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import RichTextListItem from './RichTextListItem';
import '../styles/ListWidget.css';

interface ListWidgetProps {
  block: IListBlock;
  onChange: (newBlock: IListBlock) => void;
  dossierId: string;
  userRole: string | null;
  chapterIndex: number;
  chapterTitle: string;
  onSuggestionCreated: (suggestion: ISuggestion) => void;
}

interface ListItemEditorProps {
  item: IListItem;
  level: number;
  index: number;
  onUpdate: (newItem: IListItem) => void;
  onDelete: () => void;
  onIndent: () => void;
  onOutdent: () => void;
  onCreateSibling: () => void;
  onRequestFocus: (id: string) => void;
  focusItemId: string | null;
  listStyle: ListStyleType;
  parentId?: string;
  // Nouvelles props pour l'enrichissement
  parentBlockId: string;
  dossierId: string;
  userRole: string | null;
  chapterIndex: number;
  chapterTitle: string;
  onSuggestionCreated: (suggestion: ISuggestion) => void;
  mode: 'editor' | 'reader';
}

interface SortableListItemProps {
  item: IListItem;
  isHovered: boolean;
  onHoverChange: (hovered: boolean) => void;
  children: React.ReactNode;
  subItems?: React.ReactNode;
}

// Composant externe pour SortableListItem - drag activé pour tous les niveaux
const SortableListItem: React.FC<SortableListItemProps> = ({
  item,
  isHovered,
  onHoverChange,
  children,
  subItems
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: item.id });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`list-item-editor hyphens-auto dark:text-mcdm-primary-100 ${isDragging ? 'dragging' : ''}`}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    >
      <div className="list-item-content">
        {/* Icône de drag visible au survol */}
        <span
          {...listeners}
          className={`drag-handle ${isHovered ? 'visible' : ''}`}
          title="Glisser pour réorganiser"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <rect x="3" y="4" width="10" height="1.5" rx="0.75" />
            <rect x="3" y="7.25" width="10" height="1.5" rx="0.75" />
            <rect x="3" y="10.5" width="10" height="1.5" rx="0.75" />
          </svg>
        </span>

        {children}
      </div>
      {subItems}
    </li>
  );
};

const ListItemEditor: React.FC<ListItemEditorProps> = ({
  item,
  level,
  index,
  onUpdate,
  onDelete,
  onIndent,
  onOutdent,
  onCreateSibling,
  onRequestFocus,
  focusItemId,
  listStyle,
  parentId = 'root',
  parentBlockId,
  dossierId,
  userRole,
  chapterIndex,
  chapterTitle,
  onSuggestionCreated,
  mode
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Handler pour la mise à jour du contenu (HTML enrichi)
  const handleContentChange = (newContent: string) => {
    onUpdate({ ...item, content: newContent });
  };

  // Handlers pour la navigation clavier
  const handleTabPress = () => {
    // Tab : Indenter (seulement si index > 0 et level < 5)
    if (index > 0 && level < 5) {
      onIndent();
    }
  };

  const handleShiftTabPress = () => {
    // Shift+Tab : Désindenter (seulement si level > 0)
    if (level > 0) {
      onOutdent();
    }
  };

  const handleEnterPress = () => {
    // Enter : Créer nouvel item au même niveau
    onCreateSibling();
  };

  const handleBackspaceAtStart = () => {
    // Priorité : Si indenté (level > 0), désindenter d'abord
    if (level > 0) {
      onOutdent();
      return;
    }
    // Sinon : Si vide ET niveau 0, supprimer
    const plainTextContent = item.content.replace(/<[^>]*>/g, '').trim();
    const isEmpty = plainTextContent === '' || item.content === '<br>' || item.content === '';
    if (isEmpty) {
      onDelete();
    }
  };

  const handleChildUpdate = useCallback((childIndex: number, newChild: IListItem) => {
    const newChildren = [...(item.children || [])];
    newChildren[childIndex] = newChild;
    onUpdate({ ...item, children: newChildren });
  }, [item, onUpdate]);

  const handleChildDelete = useCallback((childIndex: number) => {
    const newChildren = (item.children || []).filter((_, i) => i !== childIndex);
    onUpdate({ ...item, children: newChildren.length > 0 ? newChildren : undefined });
  }, [item, onUpdate]);

  const handleChildIndent = useCallback((childIndex: number) => {
    if (childIndex === 0 || !item.children) return;

    const newChildren = [...item.children];
    const childToMove = newChildren[childIndex];

    // Créer une copie immutable du previousChild
    const updatedPreviousChild = {
      ...newChildren[childIndex - 1],
      children: [
        ...(newChildren[childIndex - 1].children || []),
        childToMove
      ]
    };

    newChildren[childIndex - 1] = updatedPreviousChild;
    newChildren.splice(childIndex, 1);

    onUpdate({ ...item, children: newChildren });
  }, [item, onUpdate]);

  const handleChildOutdent = useCallback((childIndex: number) => {
    if (!item.children) return;

    // FIX: Implémenter correctement le outdent
    // L'enfant doit devenir un frère de l'item actuel (remonter d'un niveau)
    // Cette logique nécessite de passer par le parent
    // Pour l'instant, on appelle juste onOutdent qui sera géré au niveau supérieur
    const newChildren = item.children.filter((_, i) => i !== childIndex);

    onUpdate({ ...item, children: newChildren.length > 0 ? newChildren : undefined });

    // Note: La logique complète de outdent nécessiterait une refonte de l'architecture
    // pour pouvoir insérer l'item au bon endroit dans la liste parent
  }, [item, onUpdate]);

  const handleChildCreateSibling = useCallback((childIndex: number) => {
    const newChildren = [...(item.children || [])];
    const newId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newItem: IListItem = {
      id: newId,
      content: '',
      children: undefined
    };
    newChildren.splice(childIndex + 1, 0, newItem);
    onUpdate({ ...item, children: newChildren });
    onRequestFocus(newId);
  }, [item, onUpdate, onRequestFocus]);

  // Sensors pour le drag des enfants
  const childSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleChildDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !item.children) return;

    const activeIndex = item.children.findIndex(child => child.id === active.id);
    const overIndex = item.children.findIndex(child => child.id === over.id);

    if (activeIndex === -1 || overIndex === -1) return;

    const newChildren = arrayMove(item.children, activeIndex, overIndex);
    onUpdate({ ...item, children: newChildren });
  }, [item, onUpdate]);

  const ListTag = listStyle === 'disc' ? 'ul' : 'ol';

  return (
    <SortableListItem
      item={item}
      isHovered={isHovered}
      onHoverChange={setIsHovered}
      subItems={
        item.children && item.children.length > 0 && (
          <DndContext
            sensors={childSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleChildDragEnd}
          >
            <SortableContext
              items={item.children.map(child => child.id)}
              strategy={verticalListSortingStrategy}
            >
              <ListTag className="ml-5" style={{ listStyleType: listStyle === 'disc' ? 'disc' : listStyle === 'decimal' ? 'decimal' : listStyle === 'upper-roman' ? 'upper-roman' : 'lower-roman' }}>
                {item.children.map((child, childIndex) => (
                  <ListItemEditor
                    key={child.id}
                    item={child}
                    level={level + 1}
                    index={childIndex}
                    onUpdate={(newChild) => handleChildUpdate(childIndex, newChild)}
                    onDelete={() => handleChildDelete(childIndex)}
                    onIndent={() => handleChildIndent(childIndex)}
                    onOutdent={() => handleChildOutdent(childIndex)}
                    onCreateSibling={() => handleChildCreateSibling(childIndex)}
                    onRequestFocus={onRequestFocus}
                    focusItemId={focusItemId}
                    listStyle={listStyle}
                    parentId={item.id}
                    parentBlockId={parentBlockId}
                    dossierId={dossierId}
                    userRole={userRole}
                    chapterIndex={chapterIndex}
                    chapterTitle={chapterTitle}
                    onSuggestionCreated={onSuggestionCreated}
                    mode={mode}
                  />
                ))}
              </ListTag>
            </SortableContext>
          </DndContext>
        )
      }
    >
      {/* Utiliser RichTextListItem pour l'enrichissement */}
      <RichTextListItem
        content={item.content}
        onChange={handleContentChange}
        mode={mode}
        itemId={item.id}
        shouldFocus={item.id === focusItemId}
        parentBlockId={parentBlockId}
        chapterIndex={chapterIndex}
        chapterTitle={chapterTitle}
        dossierId={dossierId}
        onSuggestionCreated={onSuggestionCreated}
        userRole={userRole}
        onTabPress={handleTabPress}
        onShiftTabPress={handleShiftTabPress}
        onEnterPress={handleEnterPress}
        onBackspaceAtStart={handleBackspaceAtStart}
      />
    </SortableListItem>
  );
};

const ListWidget: React.FC<ListWidgetProps> = ({
  block,
  onChange,
  dossierId,
  userRole,
  chapterIndex,
  chapterTitle,
  onSuggestionCreated
}) => {
  const { editMode: currentState } = useDossier();
  const isEditing = currentState === 'editor';
  const mode: 'editor' | 'reader' = isEditing ? 'editor' : 'reader';
  const [showTypeSelector, setShowTypeSelector] = useState(false);

  // État pour gérer l'ID de l'item qui doit recevoir le focus
  const [focusItemId, setFocusItemId] = useState<string | null>(null);

  // Reset focusId après un court délai pour éviter les conflits futurs
  useEffect(() => {
    if (focusItemId) {
      const timer = setTimeout(() => setFocusItemId(null), 500);
      return () => clearTimeout(timer);
    }
  }, [focusItemId]);

  const handleItemUpdate = (index: number, newItem: IListItem) => {
    const newItems = [...block.items];
    newItems[index] = newItem;
    onChange({ ...block, items: newItems });
  };

  const handleItemDelete = (index: number) => {
    const newItems = block.items.filter((_, i) => i !== index);
    onChange({ ...block, items: newItems.length > 0 ? newItems : [] });
  };

  const handleItemIndent = (index: number) => {
    if (index === 0) return;

    const newItems = [...block.items];
    const itemToMove = newItems[index];

    // Créer une copie immutable du previousItem
    const updatedPreviousItem = {
      ...newItems[index - 1],
      children: [
        ...(newItems[index - 1].children || []),
        itemToMove
      ]
    };

    newItems[index - 1] = updatedPreviousItem;
    newItems.splice(index, 1);

    onChange({ ...block, items: newItems });
  };

  const handleItemOutdent = () => {
    // Au niveau racine, on ne peut pas désindenter davantage
    // Ne rien faire
  };

  const handleItemCreateSibling = (index: number) => {
    const newItems = [...block.items];
    const newId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newItem: IListItem = {
      id: newId,
      content: '',
      children: undefined
    };
    newItems.splice(index + 1, 0, newItem);
    onChange({ ...block, items: newItems });
    // Déclencher le focus sur le nouvel item
    setFocusItemId(newId);
  };

  // Sensors pour @dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeIndex = block.items.findIndex(item => item.id === active.id);
    const overIndex = block.items.findIndex(item => item.id === over.id);

    if (activeIndex === -1 || overIndex === -1) return;

    const newItems = arrayMove(block.items, activeIndex, overIndex);
    onChange({ ...block, items: newItems });
  };

  const getEffectiveListStyle = (): ListStyleType => {
    if (block.listStyle) return block.listStyle;
    return block.ordered ? 'decimal' : 'disc';
  };

  const getListTag = (style: ListStyleType) => style === 'disc' ? 'ul' : 'ol';

  const getListStyleInline = (style: ListStyleType): React.CSSProperties => ({
    listStyleType: style === 'disc' ? 'disc' : style === 'decimal' ? 'decimal' : style === 'upper-roman' ? 'upper-roman' : 'lower-roman',
  });

  const getStyleLabel = (style: ListStyleType) => {
    switch (style) {
      case 'disc': return '•';
      case 'decimal': return '1.';
      case 'upper-roman': return 'I.';
      case 'lower-roman': return 'i.';
    }
  };

  const effectiveStyle = getEffectiveListStyle();

  // Rendu récursif pour le mode lecture avec enrichissement
  const renderListItems = (items: IListItem[], style: ListStyleType) => {
    const ListTag = getListTag(style);

    return (
      <ListTag className="ml-5 space-y-1" style={getListStyleInline(style)}>
        {items.map((item) => (
          <li key={item.id} className="hyphens-auto dark:text-mcdm-primary-100">
            <RichTextListItem
              content={item.content}
              onChange={() => { }} // En mode reader, pas de modification
              mode="reader"
              itemId={item.id}
              parentBlockId={block._id}
              chapterIndex={chapterIndex}
              chapterTitle={chapterTitle}
              dossierId={dossierId}
              onSuggestionCreated={onSuggestionCreated}
              userRole={userRole}
            />
            {item.children && item.children.length > 0 && (
              <div className="mt-1">
                {renderListItems(item.children, style)}
              </div>
            )}
          </li>
        ))}
      </ListTag>
    );
  };

  const ListTag = getListTag(effectiveStyle);

  if (!isEditing) {
    return (
      <div className="list-widget py-2">
        {renderListItems(block.items, effectiveStyle)}
      </div>
    );
  }

  return (
    <div className="list-widget-editor relative py-2">
      <div className="list-type-controls">
        <button
          onClick={() => setShowTypeSelector(!showTypeSelector)}
          className="list-type-button"
          title="Type de liste"
        >
          {getStyleLabel(effectiveStyle)}
        </button>

        {showTypeSelector && (
          <div className="list-type-selector">
            {([
              { style: 'disc' as ListStyleType, label: '• Liste à puces' },
              { style: 'decimal' as ListStyleType, label: '1. Liste numérotée' },
              { style: 'upper-roman' as ListStyleType, label: 'I. Romain majuscule' },
              { style: 'lower-roman' as ListStyleType, label: 'i. Romain minuscule' },
            ]).map(({ style, label }) => (
              <button
                key={style}
                onClick={() => {
                  onChange({ ...block, listStyle: style, ordered: undefined });
                  setShowTypeSelector(false);
                }}
                className={`type-option ${effectiveStyle === style ? 'active' : ''}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={block.items.map(item => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <ListTag className="ml-5 space-y-1" style={getListStyleInline(effectiveStyle)}>
            {block.items.map((item, index) => (
              <ListItemEditor
                key={item.id}
                item={item}
                level={0}
                index={index}
                onUpdate={(newItem) => handleItemUpdate(index, newItem)}
                onDelete={() => handleItemDelete(index)}
                onIndent={() => handleItemIndent(index)}
                onOutdent={handleItemOutdent}
                onCreateSibling={() => handleItemCreateSibling(index)}
                onRequestFocus={setFocusItemId}
                focusItemId={focusItemId}
                listStyle={effectiveStyle}
                parentBlockId={block._id}
                dossierId={dossierId}
                userRole={userRole}
                chapterIndex={chapterIndex}
                chapterTitle={chapterTitle}
                onSuggestionCreated={onSuggestionCreated}
                mode={mode}
              />
            ))}
          </ListTag>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ListWidget;